import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  UPLOAD_POLICIES,
  UPLOAD_PURPOSE_MARKETPLACE_CUSTOM_ASSET,
  validateUploadMeta,
} from "@nexus/contracts";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Label,
  ToggleGroup,
  ToggleGroupItem,
} from "@nexus/ui";

import { runCustomAssetUploadFlow } from "@/features/marketplace/api/custom-asset-upload.api";
import type { CustomAssetKind } from "@/features/marketplace/lib/custom-asset";
import { getMarketplaceCustomAssetUploadConstraints } from "@/features/marketplace/lib/marketplace-custom-asset-upload-constraints";
import { resolveCustomAssetContentType } from "@/features/marketplace/lib/resolve-custom-asset-content-type";
import { UploadDropzone } from "@/features/preflight/components/UploadDropzone/UploadDropzone";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { useCreateCustomBriefScript } from "@/lib/queries";

const EYEBROW_LABEL =
  "text-muted-foreground text-[11px] font-semibold tracking-wider uppercase";

const CUSTOM_ASSET_POLICY = UPLOAD_POLICIES[UPLOAD_PURPOSE_MARKETPLACE_CUSTOM_ASSET];
const CUSTOM_ASSET_UPLOAD = getMarketplaceCustomAssetUploadConstraints();

export interface AddCustomBriefDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
}

export function AddCustomBriefDialog({
  open,
  onOpenChange,
  setId,
}: AddCustomBriefDialogProps) {
  const { t } = useTranslation();
  const createBrief = useCreateCustomBriefScript();
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const titleId = useId();

  const [title, setTitle] = useState("");
  const [assetKind, setAssetKind] = useState<CustomAssetKind>("BRIEF");
  const [file, setFile] = useState<File | null>(null);
  const [fileRejection, setFileRejection] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const trimmedTitle = title.trim();
  const canSubmit =
    trimmedTitle !== "" &&
    file != null &&
    !createBrief.isPending &&
    !uploading &&
    Boolean(activeTenantId);

  const resetForm = () => {
    setTitle("");
    setAssetKind("BRIEF");
    setFile(null);
    setFileRejection(null);
    setUploading(false);
  };

  const handleFiles = (files: FileList | File[]) => {
    const nextFile = files[0];
    if (!nextFile) return;

    const contentType = resolveCustomAssetContentType(nextFile, CUSTOM_ASSET_POLICY);
    const validation = validateUploadMeta(
      { contentType, sizeBytes: nextFile.size },
      CUSTOM_ASSET_POLICY,
    );

    if (!validation.ok) {
      setFile(null);
      setFileRejection(
        t("marketplace.customBrief.fileRejected", {
          fileName: nextFile.name,
          reason: validation.reason.replace(/_/g, " ").toLowerCase(),
          defaultValue: "{{fileName}} — {{reason}}",
        }),
      );
      return;
    }

    setFileRejection(null);
    setFile(nextFile);
  };

  const handleSubmit = async () => {
    if (!canSubmit || !activeTenantId || !file) return;
    setUploading(true);
    try {
      const confirmed = await runCustomAssetUploadFlow({ tenantId: activeTenantId, file });
      createBrief.mutate(
        {
          setId,
          payload: {
            title: trimmedTitle,
            uploadId: confirmed.uploadId,
            assetKind,
          },
        },
        {
          onSuccess: () => {
            resetForm();
            onOpenChange(false);
          },
          onSettled: () => setUploading(false),
        },
      );
    } catch {
      setUploading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetForm();
        onOpenChange(next);
      }}
    >
      <DialogContent variant="form" className="sm:max-w-lg">
        <DialogHeader className="border-border -mx-5 border-b px-5 pb-4">
          <DialogTitle>
            {t("marketplace.customBrief.title", "Add Custom Script/Brief")}
          </DialogTitle>
          <DialogDescription>
            {t(
              "marketplace.customBrief.description",
              "Upload a DOCX or XLSX file for your custom script or brief.",
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] overflow-y-auto">
          <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor={titleId} className={EYEBROW_LABEL}>
                    {t("marketplace.customBrief.titleLabel", "Title")}
                  </Label>
                  <Input
                    id={titleId}
                    value={title}
                    required
                    disabled={uploading || createBrief.isPending}
                    onChange={(event) => setTitle(event.target.value)}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <Label className={EYEBROW_LABEL}>
                    {t("marketplace.customBrief.kindLabel", "Type")}
                  </Label>
                  <ToggleGroup
                    type="single"
                    value={assetKind}
                    disabled={uploading || createBrief.isPending}
                    onValueChange={(value) => {
                      if (value === "SCRIPT" || value === "BRIEF") setAssetKind(value);
                    }}
                  >
                    <ToggleGroupItem
                      value="BRIEF"
                      aria-label={t("marketplace.customBrief.kindBrief", "Brief")}
                    >
                      {t("marketplace.customBrief.kindBrief", "Brief")}
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="SCRIPT"
                      aria-label={t("marketplace.customBrief.kindScript", "Script")}
                    >
                      {t("marketplace.customBrief.kindScript", "Script")}
                    </ToggleGroupItem>
                  </ToggleGroup>
                </div>
              </div>
            </section>

            <div className="border-border border-t" role="presentation" />

            <section className="flex flex-col gap-4">
              <Label className={EYEBROW_LABEL}>
                {t("marketplace.customBrief.fileLabel", "File")}
              </Label>

              <UploadDropzone
                onFiles={handleFiles}
                accept={CUSTOM_ASSET_UPLOAD.accept}
                multiple={false}
                disabled={uploading || createBrief.isPending}
                titleText={t(
                  "marketplace.customBrief.dropzoneTitle",
                  "Drag & drop a document here",
                )}
                constraintsText={t("marketplace.customBrief.dropzoneConstraints", {
                  formats: CUSTOM_ASSET_UPLOAD.formats,
                  maxSize: CUSTOM_ASSET_UPLOAD.maxSize,
                  defaultValue: "{{formats}} · Up to {{maxSize}}",
                })}
                buttonLabel={
                  file
                    ? t("marketplace.customBrief.dropzoneReplace", "Replace file")
                    : t("marketplace.customBrief.dropzoneBrowse", "Browse files")
                }
              />

              {fileRejection ? (
                <p className="text-error flex items-center gap-1.5 text-xs">
                  <Icon name="alert-circle" size="xs" />
                  <span>{fileRejection}</span>
                </p>
              ) : null}

              {file ? (
                <p className="text-muted-foreground text-xs">{file.name}</p>
              ) : null}
            </section>

            <div className="border-border flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-end">
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={uploading || createBrief.isPending}
                  onClick={() => onOpenChange(false)}
                >
                  {t("common.cancel", "Cancel")}
                </Button>
                <Button
                  type="button"
                  disabled={!canSubmit}
                  onClick={() => void handleSubmit()}
                >
                  {uploading || createBrief.isPending
                    ? t("marketplace.customBrief.submitting", "Adding…")
                    : t("marketplace.customBrief.submit", "Add")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
